import React, { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { normalizeMaterialJpl } from '../../../utils/numberNormalizer'

type CertificateMaterial = {
  topic: string
  jpl: string
  speaker: string
}

interface CertificateMaterialsInputProps {
  value?: string | null
  onChange: (value: string) => void
  topicOnly?: boolean
  fallbackTopics?: string[]
}

const parseRows = (value?: string | null): CertificateMaterial[] => {
  if (!value) return [{ topic: '', jpl: '', speaker: '' }]

  const trimmedValue = value.trim()
  if (
    !trimmedValue ||
    trimmedValue === '[]' ||
    trimmedValue === '"[]"' ||
    trimmedValue.toLowerCase() === 'null'
  ) {
    return [{ topic: '', jpl: '', speaker: '' }]
  }

  if (trimmedValue.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmedValue)
      if (Array.isArray(parsed)) {
        const rows = parsed.map((item) => ({
          topic: item?.topic || '',
          jpl: normalizeMaterialJpl(item?.jpl || ''),
          speaker: item?.speaker || '',
        }))

        return rows.length ? rows : [{ topic: '', jpl: '', speaker: '' }]
      }
    } catch {
      // Fall back to the pipe-separated format below.
    }
  }

  const rows = value
    .split('\n')
    .map((line) => {
      const [topic = '', jpl = '', speaker = ''] = line
        .split('|')
        .map((part) => part.trim())

      return { topic, jpl: normalizeMaterialJpl(jpl), speaker }
    })

  return rows.length ? rows : [{ topic: '', jpl: '', speaker: '' }]
}

const serializeRows = (rows: CertificateMaterial[]) =>
  rows
    .map((row) => [row.topic, row.jpl, row.speaker].join(' | '))
    .join('\n')

const serializeTopicOnlyRows = (rows: CertificateMaterial[]) =>
  rows.map((row) => row.topic).join('\n')

const getFallbackRows = (fallbackTopics?: string[]): CertificateMaterial[] =>
  (fallbackTopics || [])
    .map((topic) => topic.trim())
    .filter(Boolean)
    .map((topic) => ({ topic, jpl: '', speaker: '' }))

const hasMaterialContent = (rows: CertificateMaterial[]) =>
  rows.some((row) => row.topic.trim() || row.jpl.trim() || row.speaker.trim())

const resizeTextarea = (element: HTMLTextAreaElement | null) => {
  if (!element) return

  element.style.height = 'auto'
  element.style.height = `${element.scrollHeight}px`
}

const CertificateMaterialsInput = ({
  value,
  onChange,
  topicOnly = false,
  fallbackTopics,
}: CertificateMaterialsInputProps) => {
  const fallbackRows = getFallbackRows(fallbackTopics)
  const parsedRows = parseRows(value)
  const rows =
    fallbackRows.length && !hasMaterialContent(parsedRows)
      ? fallbackRows
      : parsedRows
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    wrapperRef.current
      ?.querySelectorAll<HTMLTextAreaElement>('.certificate-material-textarea')
      .forEach(resizeTextarea)
  }, [value])

  const updateRow = (
    index: number,
    field: keyof CertificateMaterial,
    nextValue: string,
  ) => {
    const nextRows = rows.map((row, rowIndex) =>
      rowIndex === index
        ? {
            ...row,
            [field]: field === 'jpl' ? normalizeMaterialJpl(nextValue) : nextValue,
          }
        : row,
    )
    onChange(topicOnly ? serializeTopicOnlyRows(nextRows) : serializeRows(nextRows))
  }

  const addRow = () => {
    const nextRows = [...rows, { topic: '', jpl: '', speaker: '' }]
    onChange(topicOnly ? serializeTopicOnlyRows(nextRows) : serializeRows(nextRows))
  }

  const removeRow = (index: number) => {
    const nextRows = rows.filter((_, rowIndex) => rowIndex !== index)
    onChange(
      topicOnly
        ? serializeTopicOnlyRows(
            nextRows.length ? nextRows : [{ topic: '', jpl: '', speaker: '' }],
          )
        : serializeRows(
            nextRows.length ? nextRows : [{ topic: '', jpl: '', speaker: '' }],
          ),
    )
  }

  return (
    <div className="table-responsive" ref={wrapperRef}>
      <style>
        {`
          .certificate-material-textarea {
            min-height: 38px;
            overflow: hidden;
            resize: none;
            white-space: pre-wrap;
            overflow-wrap: anywhere;
            word-break: break-word;
          }
        `}
      </style>
      <table className="table table-bordered align-middle mb-2">
        <thead>
          <tr>
            <th style={{ width: topicOnly ? 'calc(100% - 48px)' : '44%' }}>
              Nama Materi
            </th>
            {!topicOnly && <th style={{ width: '16%' }}>JPL</th>}
            {!topicOnly && <th style={{ width: '34%' }}>Narasumber</th>}
            <th style={{ width: 48 }} />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index}>
              <td>
                <textarea
                  className="form-control certificate-material-textarea"
                  value={row.topic}
                  placeholder="Contoh: Materi A"
                  rows={1}
                  onChange={(event) =>
                    updateRow(index, 'topic', event.target.value)
                  }
                  onInput={(event) => resizeTextarea(event.currentTarget)}
                />
              </td>
              {!topicOnly && (
                <td>
                  <input
                    type="text"
                    className="form-control"
                    value={row.jpl}
                    placeholder="2 JP"
                    onChange={(event) =>
                      updateRow(index, 'jpl', event.target.value)
                    }
                  />
                </td>
              )}
              {!topicOnly && (
                <td>
                  <textarea
                    className="form-control certificate-material-textarea"
                    value={row.speaker}
                    placeholder="apt. Nama Narasumber"
                    rows={1}
                    onChange={(event) =>
                      updateRow(index, 'speaker', event.target.value)
                    }
                    onInput={(event) => resizeTextarea(event.currentTarget)}
                  />
                </td>
              )}
              <td className="text-center">
                <Link
                  to="#"
                  className="link-trash delete-btn1"
                  onClick={() => removeRow(index)}
                >
                  <i className="isax isax-trash fs-16" />
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="d-flex justify-content-end">
        <Link
          to="#"
          className="d-flex align-items-center add-new-topic"
          onClick={addRow}
        >
          <i className="isax isax-add me-1" /> Tambah Materi
        </Link>
      </div>
    </div>
  )
}

export default CertificateMaterialsInput
